import React, { useState, useRef, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import axiosInstance from "../utils/axiosInstance";

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("rw-RW", { year: "numeric", month: "long", day: "numeric" }); }
  catch { return d; }
};

const INZEGO = ["Umudugudu", "Akagari", "Umurenge", "Akarere", "Intara"];

const ReceiverSignaturePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const sigPadRef = useRef(null);
  const fileInputRef = useRef(null);

  const [raporo, setRaporo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sigMode, setSigMode] = useState("draw"); // draw | upload
  const [uploadedImg, setUploadedImg] = useState(null);
  const [alreadySigned, setAlreadySigned] = useState(false);

  const [form, setForm] = useState({
    amazina: "",
    inshingano: "",
    inzego: "Umudugudu",
    itariki: new Date().toISOString().split("T")[0],
  });
  const [errors, setErrors] = useState({});

  const fetchRaporo = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, sRes] = await Promise.all([
        axiosInstance.get(`/raporo/${id}/view`),
        axiosInstance.get(`/raporo/${id}/signatures`).catch(() => ({ data: {} })),
      ]);
      setRaporo(rRes.data);
      if (sRes.data?.receiver_signature) {
        setAlreadySigned(true);
      }
      // Pre-fill name from profile if available
      const profileRes = await axiosInstance.get("/profile").catch(() => null);
      if (profileRes?.data) {
        // Try to get fullname from messaging/me
        const meRes = await axiosInstance.get("/messaging/me").catch(() => null);
        if (meRes?.data?.fullname) {
          setForm(f => ({ ...f, amazina: meRes.data.fullname, inzego: meRes.data.role || "Umudugudu" }));
        }
      }
    } catch {
      toast.error("Ntibyashobotse kubona raporo.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchRaporo(); }, [fetchRaporo]);

  const validate = () => {
    const e = {};
    if (!form.amazina.trim()) e.amazina = "Amazina arakenewe";
    if (!form.inshingano.trim()) e.inshingano = "Inshingano irakenewe";
    if (!form.inzego) e.inzego = "Inzego irakenewe";
    if (sigMode === "draw") {
      if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
        e.signature = "Shyiraho umukono mbere yo kubika";
      }
    }
    if (sigMode === "upload" && !uploadedImg) {
      e.signature = "Shyiraho ifoto y'umukono";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Ifoto gusa irakenewe (JPG, PNG)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setUploadedImg(ev.target.result);
    reader.readAsDataURL(file);
  };

  const getSignatureDataURL = () => {
    if (!sigPadRef.current) return null;
    // Get the raw canvas element from react-signature-canvas
    const rawCanvas = sigPadRef.current.getCanvas();
    if (!rawCanvas) return null;
    // Redraw onto a new canvas at exact pixel size to avoid scaling artifacts
    const out = document.createElement("canvas");
    out.width = rawCanvas.width;
    out.height = rawCanvas.height;
    const ctx = out.getContext("2d");
    // White background so JPEG has no transparency issues
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(rawCanvas, 0, 0);
    return out.toDataURL("image/jpeg", 0.6);
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (saving) return;
    setSaving(true);
    try {
      let sigData;
      if (sigMode === "draw") {
        if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
          toast.error("Shyiraho umukono mbere yo kubika");
          setSaving(false);
          return;
        }
        sigData = getSignatureDataURL();
        if (!sigData || sigData.length < 1000) {
          toast.error("Umukono ntabwo wabashije gufatwa. Ongera ugerageze.");
          setSaving(false);
          return;
        }
      } else {
        sigData = uploadedImg;
      }

      if (!sigData || !sigData.startsWith("data:")) {
        toast.error("Umukono ntabwo wabashije gufatwa. Ongera ugerageze.");
        setSaving(false);
        return;
      }

      const res = await axiosInstance.post(`/raporo/${id}/sign-receiver`, {
        signature_image: sigData,
        receiver_name: form.amazina.trim(),
        receiver_role: form.inshingano.trim(),
        receiver_level: form.inzego,
        receiver_signed_at: form.itariki,
      });

      toast.success("✅ Raporo yasinywe neza!", { autoClose: 2000 });
      setTimeout(() => navigate(`/view-report/${id}`), 2200);
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.error || err?.response?.data?.detail;

      if (!navigator.onLine) {
        toast.error("Nta interineti. Reba iyunganira maze ugerageze nanone.");
      } else if (status === 401) {
        toast.error("Igihe cya konti yawe kirangiye. Injira nanone.");
        localStorage.removeItem("token");
        setTimeout(() => { window.location.href = "/mudugudu"; }, 1500);
      } else if (status === 404) {
        toast.error("Raporo ntiboneka kuri seriveri. Gerageza nanone.");
      } else if (status === 413) {
        toast.error("Umukono munini cyane. Gerageza umukono muto.");
      } else if (status === 400) {
        toast.error(serverMsg || "Amakuru ntabwo yuzuye. Gerageza nanone.");
      } else if (status >= 500) {
        toast.error(serverMsg || "Ikibazo cya seriveri. Gerageza nanone.");
      } else {
        toast.error(serverMsg || `Habaye ikibazo (${status || 'network'}). Gerageza nanone.`);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={S.center}>
      <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} />
      <p style={{ marginTop: "1rem", color: "#64748b", fontFamily: "Nunito Sans,sans-serif" }}>Raporo irimo gutegurwa...</p>
    </div>
  );

  if (!raporo) return (
    <div style={S.center}>
      <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "3rem", color: "#dc2626" }} />
      <p style={{ color: "#dc2626", fontWeight: 600, fontFamily: "Nunito Sans,sans-serif", marginTop: "1rem" }}>Raporo ntiboneka.</p>
      <button onClick={() => navigate(-1)} style={S.btnSecondary}><i className="bi bi-arrow-left" /> Subira Inyuma</button>
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;500;600;700;800&display=swap');
        body { background: #e8edf5; margin: 0; font-family: 'Nunito Sans', sans-serif; }
        .rsp-input { width: 100%; padding: 0.75rem 1rem; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 0.92rem; color: #1e293b; background: #f8fafc; outline: none; transition: border-color 0.2s; font-family: 'Nunito Sans', sans-serif; }
        .rsp-input:focus { border-color: #0f766e; box-shadow: 0 0 0 3px rgba(15,118,110,0.12); }
        .rsp-input.error { border-color: #dc2626; }
        .rsp-label { font-size: 0.72rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 5px; display: block; }
        .rsp-error { font-size: 0.75rem; color: #dc2626; margin-top: 4px; display: flex; align-items: center; gap: 4px; }
        .tab-btn { flex: 1; padding: 0.65rem; border: 1.5px solid #e2e8f0; border-radius: 10px; background: #f8fafc; color: #64748b; font-weight: 600; font-size: 0.85rem; cursor: pointer; transition: all 0.2s; font-family: 'Nunito Sans', sans-serif; display: flex; align-items: center; justify-content: center; gap: 6px; }
        .tab-btn.active { background: linear-gradient(135deg,#0f766e,#0d9488); color: #fff; border-color: transparent; box-shadow: 0 4px 12px rgba(15,118,110,0.3); }
        @media (max-width: 600px) { .rsp-grid { grid-template-columns: 1fr !important; } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        .rsp-card { animation: fadeUp 0.3s ease; }
      `}</style>

      <ToastContainer position="top-right" autoClose={3500} hideProgressBar={false} theme="colored" />

      {/* Top bar */}
      <div style={{ background: "#1e293b", padding: "0.75rem 1.5rem", display: "flex", alignItems: "center", gap: "1rem", position: "sticky", top: 0, zIndex: 100, boxShadow: "0 2px 8px rgba(0,0,0,0.2)" }}>
        <button onClick={() => navigate(-1)} style={S.btnSecondary}>
          <i className="bi bi-arrow-left" /> Subira Inyuma
        </button>
        <div style={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem" }}>
          <i className="bi bi-pen-fill" style={{ color: "#0d9488", marginRight: 8 }} />
          Shyiraho Umukono w'Uwakiriye Raporo
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: "2rem auto", padding: "0 1rem 4rem" }}>

        {/* Report info card */}
        <div className="rsp-card" style={{ background: "#fff", borderRadius: 16, padding: "1.25rem 1.5rem", marginBottom: "1.25rem", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", border: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 }}>Raporo</div>
          <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "#1e293b", marginBottom: 6 }}>{raporo.umutwe_wa_raporo || "Raporo"}</div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.82rem", color: "#64748b" }}><i className="bi bi-person-fill" style={{ color: "#1d4ed8", marginRight: 4 }} />{raporo.yakozwe_na || "—"}</span>
            <span style={{ fontSize: "0.82rem", color: "#64748b" }}><i className="bi bi-calendar-fill" style={{ color: "#b45309", marginRight: 4 }} />{fmtDate(raporo.itariki_ya_raporo)}</span>
            <span style={{ fontSize: "0.82rem", color: "#64748b" }}><i className="bi bi-geo-alt-fill" style={{ color: "#0f766e", marginRight: 4 }} />{raporo.umudugudu || "—"}</span>
          </div>
        </div>

        {/* Already signed notice */}
        {alreadySigned && (
          <div style={{ background: "#dcfce7", border: "1px solid #bbf7d0", borderRadius: 12, padding: "1rem 1.25rem", marginBottom: "1.25rem", display: "flex", alignItems: "center", gap: 10 }}>
            <i className="bi bi-check-circle-fill" style={{ color: "#15803d", fontSize: "1.2rem" }} />
            <div>
              <div style={{ fontWeight: 700, color: "#15803d", fontSize: "0.9rem" }}>Raporo Yamaze Gusinywa</div>
              <div style={{ fontSize: "0.8rem", color: "#166534" }}>Iyi raporo yamaze gushyirwaho umukono w'uwakiriye. Ushobora gusinyisha nanone niba bikenewe.</div>
            </div>
          </div>
        )}

        {/* Main form card */}
        <div className="rsp-card" style={{ background: "#fff", borderRadius: 16, padding: "1.75rem", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", border: "1px solid #e2e8f0" }}>

          {/* Section header */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: "1.75rem", paddingBottom: "1rem", borderBottom: "2px solid #f1f5f9" }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: "linear-gradient(135deg,#0f766e,#0d9488)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="bi bi-pen-fill" style={{ color: "#fff", fontSize: "1.1rem" }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "#1e293b" }}>2. Umukono w'Uwakiriye Raporo</div>
              <div style={{ fontSize: "0.78rem", color: "#64748b" }}>Uzuza amakuru yawe hanyuma ushyireho umukono</div>
            </div>
          </div>

          {/* Form fields */}
          <div className="rsp-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1.25rem" }}>
            <div>
              <label className="rsp-label">Amazina *</label>
              <input
                className={`rsp-input${errors.amazina ? " error" : ""}`}
                placeholder="Amazina yawe yose"
                value={form.amazina}
                onChange={e => setForm(f => ({ ...f, amazina: e.target.value }))}
              />
              {errors.amazina && <div className="rsp-error"><i className="bi bi-exclamation-circle-fill" />{errors.amazina}</div>}
            </div>
            <div>
              <label className="rsp-label">Inshingano *</label>
              <input
                className={`rsp-input${errors.inshingano ? " error" : ""}`}
                placeholder="Inshingano yawe (ex: Umuyobozi)"
                value={form.inshingano}
                onChange={e => setForm(f => ({ ...f, inshingano: e.target.value }))}
              />
              {errors.inshingano && <div className="rsp-error"><i className="bi bi-exclamation-circle-fill" />{errors.inshingano}</div>}
            </div>
            <div>
              <label className="rsp-label">Inzego *</label>
              <select
                className={`rsp-input${errors.inzego ? " error" : ""}`}
                value={form.inzego}
                onChange={e => setForm(f => ({ ...f, inzego: e.target.value }))}
                style={{ cursor: "pointer" }}
              >
                {INZEGO.map(z => <option key={z} value={z}>{z}</option>)}
              </select>
              {errors.inzego && <div className="rsp-error"><i className="bi bi-exclamation-circle-fill" />{errors.inzego}</div>}
            </div>
            <div>
              <label className="rsp-label">Itariki yo Gusinya</label>
              <input
                type="date"
                className="rsp-input"
                value={form.itariki}
                onChange={e => setForm(f => ({ ...f, itariki: e.target.value }))}
              />
            </div>
          </div>

          {/* Signature mode tabs */}
          <div style={{ marginBottom: "1rem" }}>
            <label className="rsp-label" style={{ marginBottom: 8 }}>Umukono *</label>
            <div style={{ display: "flex", gap: 8, marginBottom: "1rem" }}>
              <button className={`tab-btn${sigMode === "draw" ? " active" : ""}`} onClick={() => setSigMode("draw")}>
                <i className="bi bi-pen-fill" /> Sinyisha Hano
              </button>
              <button className={`tab-btn${sigMode === "upload" ? " active" : ""}`} onClick={() => setSigMode("upload")}>
                <i className="bi bi-upload" /> Shyiraho Ifoto
              </button>
            </div>

            {sigMode === "draw" && (
              <div
                style={{ border: "2px dashed #0f766e", borderRadius: 12, background: "#ffffff", overflow: "hidden", position: "relative" }}
              >
                <SignatureCanvas
                  ref={sigPadRef}
                  penColor="#0f766e"
                  backgroundColor="#ffffff"
                  canvasProps={{
                    style: { width: "100%", height: 180, display: "block", cursor: "crosshair", touchAction: "none" }
                  }}
                />
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", fontSize: "0.78rem", color: "#cbd5e1", pointerEvents: "none", userSelect: "none", whiteSpace: "nowrap" }}>
                  Shyiraho umukono hano ✍️
                </div>
                <button
                  type="button"
                  onClick={() => sigPadRef.current?.clear()}
                  style={{ position: "absolute", top: 8, right: 8, background: "rgba(255,255,255,0.9)", border: "1px solid #e2e8f0", borderRadius: 8, padding: "4px 10px", fontSize: "0.75rem", cursor: "pointer", color: "#64748b", fontWeight: 600 }}
                >
                  <i className="bi bi-eraser-fill" /> Siba
                </button>
              </div>
            )}

            {sigMode === "upload" && (
              <div>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  style={{ border: "2px dashed #0f766e", borderRadius: 12, background: "#f0fdf4", padding: "2rem", textAlign: "center", cursor: "pointer", transition: "background 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#dcfce7"}
                  onMouseLeave={e => e.currentTarget.style.background = "#f0fdf4"}
                >
                  {uploadedImg ? (
                    <img src={uploadedImg} alt="Umukono" style={{ maxHeight: 120, maxWidth: "100%", objectFit: "contain", borderRadius: 8 }} />
                  ) : (
                    <>
                      <i className="bi bi-cloud-upload-fill" style={{ fontSize: "2rem", color: "#0f766e", display: "block", marginBottom: 8 }} />
                      <div style={{ fontWeight: 600, color: "#0f766e", fontSize: "0.9rem" }}>Kanda hano gushyiraho ifoto y'umukono</div>
                      <div style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: 4 }}>JPG, PNG — Max 2MB</div>
                    </>
                  )}
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileUpload} />
                {uploadedImg && (
                  <button onClick={() => setUploadedImg(null)} style={{ marginTop: 8, background: "none", border: "1px solid #e2e8f0", borderRadius: 8, padding: "4px 12px", fontSize: "0.78rem", cursor: "pointer", color: "#64748b" }}>
                    <i className="bi bi-x-circle" /> Siba ifoto
                  </button>
                )}
              </div>
            )}

            {errors.signature && <div className="rsp-error" style={{ marginTop: 8 }}><i className="bi bi-exclamation-circle-fill" />{errors.signature}</div>}
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 10, marginTop: "1.5rem", paddingTop: "1.25rem", borderTop: "1px solid #f1f5f9" }}>
            <button onClick={() => navigate(-1)} style={{ flex: 1, padding: "0.85rem", border: "1.5px solid #e2e8f0", borderRadius: 12, background: "#f8fafc", color: "#64748b", fontWeight: 700, cursor: "pointer", fontSize: "0.9rem", fontFamily: "Nunito Sans,sans-serif" }}>
              <i className="bi bi-x-lg" /> Reka
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ flex: 2, padding: "0.85rem", border: "none", borderRadius: 12, background: saving ? "#94a3b8" : "linear-gradient(135deg,#0f766e,#0d9488)", color: "#fff", fontWeight: 800, cursor: saving ? "not-allowed" : "pointer", fontSize: "0.9rem", fontFamily: "Nunito Sans,sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: saving ? "none" : "0 4px 14px rgba(15,118,110,0.35)", transition: "all 0.2s" }}
            >
              {saving
                ? <><span className="spinner-border spinner-border-sm" style={{ width: "1rem", height: "1rem" }} /> Biratunganywa...</>
                : <><i className="bi bi-check-circle-fill" /> Bika Umukono</>}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: "2rem", fontSize: "0.75rem", color: "#94a3b8", fontFamily: "Nunito Sans,sans-serif" }}>
          <p style={{ margin: 0 }}>Iyi raporo yakozwe hakoreshejwe Sisitemu y'Umudugudu</p>
          <p style={{ margin: 0 }}>© {new Date().getFullYear()} — Raporo Yemewe</p>
        </div>
      </div>
    </>
  );
};

const S = {
  center: { minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#e8edf5", gap: "0.5rem" },
  btnSecondary: { display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.55rem 1.1rem", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 10, color: "white", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer", fontFamily: "Nunito Sans,sans-serif" },
};

export default ReceiverSignaturePage;
